class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      if (event.defaultPrevented || !this.classList.contains('active') || this.inert || document.querySelector('dialog[open]')) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay' || event.target.closest('[data-bag-continue]')) this.close();
      if (event.target.closest('[data-bag-scent-back]') && !this.scentSaving) this.resetScentStep();
      const choice = event.target.closest('[data-bag-scent-choice]');
      if (choice) this.saveScentChoice(choice.dataset.bagScentChoice);
    });
    this.addEventListener('change', (event) => {
      if (event.target.id === 'CartDrawer-Note') this.queueNoteUpdate(event.target.value).catch(() => {});
    });
    this.addEventListener('submit', (event) => {
      if (event.target.id !== 'CartDrawer-Form' || event.submitter?.name !== 'checkout' || this.scentConfirmed || !this.querySelector('[data-bag-scent-step]')) return;
      event.preventDefault();
      this.showScentStep();
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.scentMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.scentMediaChange = () => {
      if (this.scentMedia.matches && this.scentAnimations?.length) this.setScentStep(Boolean(this.scentTarget), true, false);
    };
    this.scentMedia.addEventListener?.('change', this.scentMediaChange);
  }

  disconnectedCallback() {
    this.scentMedia?.removeEventListener?.('change', this.scentMediaChange);
    this.cancelScentMotion();
    this.checkoutRun = (this.checkoutRun || 0) + 1;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (this.scentStepNode && !this.scentStepNode.isConnected) this.resetScentStep(false);
    if (this.classList.contains('active')) {
      this.focusDrawer();
      return;
    }
    if (this.openTimer) return;
    this.inert = false;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.classList.add('animate', 'active');
      this.focusDrawer();
    });
    this.ownsBodyLock = !document.body.classList.contains('overflow-hidden');
    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.classList.remove('active');
    const returnTarget = this.activeElement?.isConnected ? this.activeElement : document.getElementById('cart-icon-bubble');
    removeTrapFocus(returnTarget);
    this.inert = true;
    if (this.ownsBodyLock) document.body.classList.remove('overflow-hidden');
    this.ownsBodyLock = false;
    this.checkoutRun = (this.checkoutRun || 0) + 1;
    this.resetScentStep(false);
  }

  focusDrawer() {
    if (document.querySelector('marame-quick-add dialog[open]')) return;
    const container = this.querySelector('.drawer__inner');
    if (container) trapFocus(container, container);
    this.querySelectorAll('marame-bag-recommendations').forEach((element) => element.load?.());
  }

  showScentStep() {
    this.setScentStep(true);
  }

  resetScentStep(focus = true) {
    this.setScentStep(false, focus, focus);
  }

  cancelScentMotion() {
    this.scentMotionRun = (this.scentMotionRun || 0) + 1;
    clearTimeout(this.scentMotionTimer);
    this.scentMotionTimer = null;
    this.scentAnimations?.forEach(animation => { animation.onfinish = null; animation.cancel(); });
    this.scentAnimations = [];
    this.scentTransitioning = false;
  }

  setScentStep(show, focus = true, animate = true) {
    const step = this.querySelector('[data-bag-scent-step]');
    const main = this.querySelector('[data-bag-main]');
    this.cancelScentMotion();
    this.scentConfirmed = false;
    this.scentTarget = show;
    this.scentStepNode = step;
    if (!step || !main) {
      if (main) { main.hidden = false; main.inert = false; }
      return;
    }
    const run = this.scentMotionRun;
    const outgoing = show ? main : step;
    const incoming = show ? step : main;
    for (const region of [main, step]) {
      for (const child of region.children) child.inert = false;
    }
    const inner = this.querySelector('.drawer__inner');
    const moving = animate && this.classList.contains('active') && !outgoing.hidden &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches && typeof outgoing.animate === 'function';
    const reveal = () => {
      if (run !== this.scentMotionRun) return;
      if (!main.isConnected || !step.isConnected) { this.cancelScentMotion(); return; }
      outgoing.hidden = true;
      outgoing.inert = true;
      incoming.hidden = false;
      incoming.inert = false;
      this.scentTransitioning = false;
      if (focus && this.classList.contains('active')) {
        trapFocus(inner, show ? step.querySelector('[data-bag-scent-title]') : this.querySelector('#CartDrawer-Checkout'));
      }
      if (!moving || !this.classList.contains('active')) return;
      const animation = incoming.animate([
        { transform: `translateX(${show ? '100%' : '-100%'})`, opacity: 0 },
        { transform: 'translateX(0)', opacity: 1 }
      ], { duration: 400, easing: 'cubic-bezier(.2,.8,.2,1)' });
      this.scentAnimations.push(animation);
      const heading = incoming.querySelector(show ? '[data-bag-scent-title]' : '.marame-bag__heading-group');
      if (heading?.animate) this.scentAnimations.push(heading.animate([
        { opacity: 0, translate: show ? '0 4px' : '-10px 0' }, { opacity: 1, translate: '0 0' }
      ], { duration: 280, easing: 'ease' }));
      animation.onfinish = () => { if (run === this.scentMotionRun) this.cancelScentMotion(); };
      this.scentMotionTimer = setTimeout(() => { if (run === this.scentMotionRun) this.cancelScentMotion(); }, 460);
    };
    if (!moving) { reveal(); return; }
    this.scentTransitioning = true;
    outgoing.inert = false;
    // Keep Close/Back reachable while the old content slides away.
    for (const child of outgoing.children) {
      if (!child.matches('.drawer__header, .marame-bag-scent__header')) child.inert = true;
    }
    incoming.inert = true;
    if (focus && inner) trapFocus(inner, inner);
    const exit = outgoing.animate([
      { transform: 'translateX(0)', opacity: 1 },
      { transform: `translateX(${show ? '-100%' : '100%'})`, opacity: 0 }
    ], { duration: 320, easing: 'cubic-bezier(.25,.1,.25,1)', fill: 'forwards' });
    this.scentAnimations.push(exit);
    const complete = () => {
      if (run !== this.scentMotionRun || !this.scentTransitioning) return;
      clearTimeout(this.scentMotionTimer);
      exit.onfinish = null;
      exit.cancel();
      reveal();
    };
    exit.onfinish = complete;
    this.scentMotionTimer = setTimeout(complete, 360);
  }

  queueNoteUpdate(note) {
    const pending = window.marameQueueCartNote ? window.marameQueueCartNote(note) : (this.noteQueue || Promise.resolve()).catch(() => {}).then(async () => {
      const response = await fetch(routes.cart_update_url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note }) });
      if (!response.ok) throw new Error('Note update failed');
      const cart = await response.json();
      if (cart.errors || String(cart.note || '') !== note) throw new Error('Note update not confirmed');
      return cart;
    });
    this.noteQueue = pending;
    pending.then(() => {
      const error = this.querySelector('[data-bag-note-error]');
      if (error) error.hidden = true;
    }, () => {
      const error = this.querySelector('[data-bag-note-error]');
      if (error) error.hidden = false;
    });
    return pending;
  }

  async saveScentChoice(choice) {
    if (this.scentSaving || this.scentTransitioning || !['yes', 'no'].includes(choice)) return;
    const step = this.querySelector('[data-bag-scent-step]');
    if (!step || step.hidden) return;
    const run = this.checkoutRun = (this.checkoutRun || 0) + 1;
    this.scentSaving = true;
    const buttons = [...step.querySelectorAll('[data-bag-scent-choice], [data-bag-scent-back]')];
    buttons.forEach((button) => { button.disabled = true; });
    const error = step.querySelector('[data-bag-scent-error]');
    const saving = step.querySelector('[data-bag-scent-saving]');
    error.hidden = true;
    saving.hidden = false;
    step.setAttribute('aria-busy', 'true');
    try {
      await (this.noteQueue || Promise.resolve()).catch(() => {});
      const input = this.querySelector('#CartDrawer-Note');
      if (window.marameQueueCartNote) {
        const updatedCart = await this.queueNoteUpdate((currentNote) => marameScentNote(currentNote, choice));
        if (input) input.value = updatedCart.note || '';
        const pageNote = document.getElementById('Cart-note');
        if (pageNote) pageNote.value = updatedCart.note || '';
      } else {
      const response = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!cart.item_count || cart.errors) throw new Error('Cart unavailable');
      const originalNote = input ? input.value : String(cart.note || '');
      const note = marameScentNote(originalNote, choice);
      await this.queueNoteUpdate(note);
      if (input) input.value = note;
      }
      if (run !== this.checkoutRun || !step.isConnected || !this.classList.contains('active')) return;
      this.scentConfirmed = true;
      try {
        this.querySelector('#CartDrawer-Form').requestSubmit(this.querySelector('#CartDrawer-Checkout'));
      } finally {
        this.scentConfirmed = false;
      }
    } catch {
      if (run === this.checkoutRun && step.isConnected) {
        error.textContent = step.dataset.errorLabel;
        error.hidden = false;
        this.scentConfirmed = false;
      }
    } finally {
      this.scentSaving = false;
      buttons.forEach((button) => { button.disabled = false; });
      saving.hidden = true;
      step.removeAttribute('aria-busy');
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function marameScentNote(note, choice) {
  const marker = 'Parcel to be unscented: true';
  const retained = String(note || '').replaceAll(marker, '').replace(/\r\n/g, '\n').trim();
  return choice === 'no' ? `${retained}${retained ? '\n' : ''}${marker}` : retained;
}

if (!customElements.get('marame-bag-recommendations')) {
  customElements.define('marame-bag-recommendations', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      this.addEventListener('click', (event) => {
        if (this.suppressDragClick) {
          this.suppressDragClick = false;
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        const control = event.target.closest('[data-bag-scroll]');
        const rail = this.querySelector('[data-bag-recommendations-rail]');
        if (!control || !rail) return;
        rail.scrollBy({ left: Number(control.dataset.bagScroll) * Math.max(160, rail.clientWidth * 0.8), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, { signal: this.events.signal, capture: true });
      this.addEventListener('pointerdown', event => this.startRailDrag(event), { signal: this.events.signal });
      this.addEventListener('pointermove', event => this.moveRailDrag(event), { signal: this.events.signal });
      this.addEventListener('pointerup', event => this.endRailDrag(event), { signal: this.events.signal });
      this.addEventListener('pointercancel', event => this.endRailDrag(event), { signal: this.events.signal });
      this.addEventListener('lostpointercapture', event => this.endRailDrag(event), { signal: this.events.signal });
      this.addEventListener('dragstart', event => {
        if (event.target.closest('[data-bag-recommendations-rail]')) event.preventDefault();
      }, { signal: this.events.signal });
      if (this.closest('cart-drawer')?.classList.contains('active')) this.load();
    }

    disconnectedCallback() {
      this.endRailDrag();
      clearTimeout(this.dragClickTimer);
      this.events?.abort();
      this.request?.abort();
      this.request = null;
    }

    startRailDrag(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const rail = event.target.closest('[data-bag-recommendations-rail]');
      if (!rail || rail.scrollWidth <= rail.clientWidth || event.target.closest('button')) return;
      this.suppressDragClick = false;
      this.drag = { rail, id: event.pointerId, startX: event.clientX, startY: event.clientY, scroll: rail.scrollLeft, active: false };
    }

    moveRailDrag(event) {
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      const delta = event.clientX - drag.startX;
      if (!drag.active) {
        if (Math.abs(event.clientY - drag.startY) > Math.abs(delta)) { this.drag = null; return; }
        if (Math.abs(delta) < 6) return;
        drag.active = true;
        drag.rail.setAttribute('data-dragging', '');
        drag.rail.setPointerCapture?.(event.pointerId);
      }
      event.preventDefault();
      drag.rail.scrollLeft = drag.scroll - delta;
    }

    endRailDrag(event) {
      const drag = this.drag;
      if (!drag || (event && event.pointerId !== drag.id)) return;
      this.drag = null;
      drag.rail.removeAttribute('data-dragging');
      if (drag.rail.hasPointerCapture?.(drag.id)) drag.rail.releasePointerCapture(drag.id);
      this.suppressDragClick = Boolean(drag.active);
      clearTimeout(this.dragClickTimer);
      this.dragClickTimer = setTimeout(() => { this.suppressDragClick = false; }, 350);
    }

    async load() {
      if (this.loaded || this.request || !this.isConnected) return;
      const request = new AbortController();
      this.request = request;
      try {
        const url = new URL(this.dataset.url, window.location.href);
        if (url.origin !== window.location.origin) throw new Error('Invalid recommendations URL');
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url.href, { signal: request.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const html = await response.text();
        if (!this.isConnected || this.request !== request) return;
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bag-recommendations-content]');
        this.loaded = true;
        if (!content?.querySelector('[data-bag-recommendation]')) return;
        this.replaceChildren(content);
        this.hidden = false;
        this.dispatchEvent(new CustomEvent('marame:bag-recommendations-ready', { bubbles: true }));
      } catch {
        if (this.request === request) this.hidden = true;
      } finally {
        if (this.request === request) this.request = null;
      }
    }
  });
}
